import AboutScene from "@/components/AboutScene";
import { useEffect, useRef } from "react";
import { useTheme } from "@/lib/theme";

const stats = [
  { v: "12+", l: "Years Experience" },
  { v: "100%", l: "Deployment" },
  { v: "5", l: "Countries" },
  { v: "10+", l: "Projects" },
];

export default function About() {
  const root = useRef(null);
  const { theme } = useTheme();

  useEffect(() => {
    const els = root.current?.querySelectorAll(".reveal");
    if (!els) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) e.target.classList.add("is-in");
        });
      },
      { threshold: 0.18 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <section
      id="about"
      data-testid="section-about"
      ref={root}
      className="relative py-28 md:py-40 overflow-hidden"
    >
      <div className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />
      <div className="absolute -top-32 -right-32 w-[460px] h-[460px] rounded-full bg-[var(--vermilion)] opacity-[0.07] blur-3xl pointer-events-none" />

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex items-center gap-3 mb-8 reveal">
          <span className="w-2 h-2 bg-[var(--lime)] rounded-full" />
          <span className="overline">— About Us</span>
        </div>

        <p className="text-[var(--text-2)] text-sm md:text-base leading-relaxed max-w-3xl mb-14 reveal">
          Helping small businesses modernize operations through websites,
          applications, automation, and AI-powered technology solutions.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
          <div className="md:col-span-7">
            <h2
              data-testid="about-headline"
              className="font-display font-black text-4xl sm:text-5xl md:text-6xl leading-[0.95] tracking-tight reveal"
            >
              Turning <span className="italic font-normal text-[var(--text-2)]">business ideas</span> <br />
              into scalable<br />
              <span className="text-[var(--vermilion)]">digital systems</span>.
            </h2>

            <div className="mt-10 space-y-6 max-w-xl">
              <p className="text-[var(--text-2)] text-base md:text-lg leading-relaxed reveal">
                At KS DevArc, we help businesses transform ideas into practical
                digital solutions tailored for growth and efficiency. From
                business websites and custom applications to lightweight SaaS
                platforms and AI-powered automation, we design and develop
                technology that simplifies operations and improves customer
                experiences.
              </p>
              <p className="text-[var(--text-2)] text-base md:text-lg leading-relaxed reveal">
                Our focus is on creating scalable, modern, and user-friendly
                systems that help businesses adapt and grow in an increasingly
                digital world.
              </p>
            </div>

            <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-6 reveal">
              {stats.map((s, i) => (
                <div
                  key={i}
                  data-testid={`about-stat-${i}`}
                  className="border-t border-[var(--border-dim)] pt-4"
                >
                  <div className="font-display font-black text-3xl md:text-4xl tracking-tight text-[var(--text-1)]">
                    {s.v}
                  </div>
                  <div className="font-mono-ui text-[10px] tracking-[0.22em] uppercase text-[var(--text-3)] mt-1">
                    {s.l}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="md:col-span-5 relative reveal">
            <div className="relative aspect-square w-full rounded-sm border border-[var(--border-dim)] bg-[var(--bg-2)] overflow-hidden">
              <AboutScene theme={theme} />
              <div className="absolute top-4 left-4 chip">
                <span className="dot" /> live_render
              </div>
              <div className="absolute bottom-4 right-4 font-mono-ui text-[10px] tracking-[0.22em] text-[var(--text-3)]">
                geo_001 / torusKnot
              </div>
            </div>

            <div className="mt-6 grid grid-cols-2 gap-4">
              <div className="border border-[var(--border-dim)] p-4 rounded-sm">
                <div className="overline">Stack</div>
                <div className="mt-2 font-display text-sm">React · Node · Python · AI APIs</div>
              </div>
              <div className="border border-[var(--border-dim)] p-4 rounded-sm">
                <div className="overline">Workflow</div>
                <div className="mt-2 font-display text-sm">Discover · Design · Develop</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
